import React, { CSSProperties, FocusEvent, forwardRef, ReactElement, ReactNode, Ref } from 'react';
import { Input, InputRefType } from '../base-input/Input';
import { DataTypeWithInvalid } from '@unifocus/js-utils';

export declare type TextInputValueType = DataTypeWithInvalid<string>;

/**
 * {@docCategory TextInput}
 */
export interface TextInputProps {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: string;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Sets the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * Hint for which keyboard to show on mobile devices.
    * @defaultvalue text
    */
   inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

   /**
    * Hint for how to handle automatic capitalization.
    * @defaultvalue sentences
    */
   autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

   /**
    * Hint for how to handle automatic completion.
    * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
    */
   autoComplete?: string | 'off' | 'on';

   /**
    * Sets the maximum number of characters that can be entered.
    */
   maxLength?: number;

   /**
    * Enable the clear feature.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The suffix element.
    */
   suffix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets the popupTrigger.
    */
   popupTrigger?: ReactElement;

   /**
    * Set this to focus on first render
    */
   autoFocus?: boolean;

   /**
    * The pattern specifies a regular expression that the <input> element's value is checked against
    */
   inputKeyFilter?: string | RegExp | ((value: string) => boolean);

   /**
    * Whether or not to trigger change events on blur
    * @defaultvalue false
    */
   triggerOnBlur?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value?: string) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const TextInput = forwardRef<InputRefType, TextInputProps>(
   (props: TextInputProps, ref: Ref<InputRefType>) => {
      return (
         <Input
            ref={ref}
            name={props.name}
            type={'text'}
            inputMode={props.inputMode || 'text'}
            autoCapitalize={props.autoCapitalize}
            autoComplete={props.autoComplete}
            placeholder={props.placeholder}
            readOnly={props.readOnly}
            disabled={props.disabled}
            width={props.width}
            minWidth={props.minWidth}
            maxWidth={props.maxWidth}
            border={props.border}
            padding={props.padding}
            allowClear={props.allowClear}
            prefix={props.prefix}
            suffix={props.suffix}
            addonBefore={props.addonBefore}
            addonAfter={props.addonAfter}
            className={props.className}
            style={props.style}
            value={props.value}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onPressEnter={props.onPressEnter}
            popupTrigger={props.popupTrigger}
            triggerOnBlur={props.triggerOnBlur}
            inputKeyFilter={props.inputKeyFilter}
         />
      );
   });
